import { withStyles, Grid, Typography, Paper, Tooltip, Button } from '@material-ui/core';
import React from 'react'; // eslint-disable-line
import styles, { StyledComponent } from '../../../../../styles/styles';
import { LabeledValue } from '../../../../../controls/labeled-value';
import { Edit } from '@material-ui/icons';
import { connect } from 'react-redux';
import { State } from '../../../../../redux/root-reducer';
import { FormDetails, getFormDetails } from '../form-details-reducer';
import { getDisplayDate } from '../../../../../shared-functions';
import { OpenModal, openModalAction } from '../../../../../form-modal/actions';
import { editLifeAssureDetails } from './life-assured-edit';
import { editPolicyHolderDetails } from './policy-holder-edit';
import { UpdateFormDetails, updateFormDetailsAction } from '../actions/update-form-details';
import { getClaim } from '../../../../../claim';
import { Nullable } from '../../../../../shared-types';
import { ClaimType } from '../../../../../claim/claim-types';

interface BaseProps {
    formDetails?: FormDetails;
    disableEditButton?: boolean;
    updateFormDetails?: UpdateFormDetails;
    type: string;
    claimType?: Nullable<ClaimType>;
}

interface Props extends BaseProps, StyledComponent {
    readonly openModal: OpenModal;
}

const personalDetailsView: React.FunctionComponent<Props> = ({
    classes,
    openModal,
    formDetails,
    updateFormDetails,
    type,
    disableEditButton,
    claimType,
}: Props) => {
    const edit = () => {
        const details = formDetails || {};
        type === 'LifeAssured'
            ? editLifeAssureDetails(openModal, details, 0, updateFormDetails)
            : editPolicyHolderDetails(openModal, details, 0, updateFormDetails);
    };

    const personalDetails =
        type === 'LifeAssured'
            ? formDetails?.lifeAssured?.personalDetails
            : formDetails?.policyHolder?.individual?.personalDetails;
    const policyHolderType = formDetails?.policyHolder?.policyHolderType;
    const isThePolicyHolderAnIndividualOrCompany =
        formDetails?.policyHolder?.isThePolicyHolderAnIndividualOrCompany;

    let header = 'Personal Details';
    if (isThePolicyHolderAnIndividualOrCompany === 'Company') {
        header = 'Company Details';
    }
    const contactDetails = formDetails?.claimant?.contactDetails;
    const companyDetails = formDetails?.policyHolder?.individual?.companyDetails;
    if (claimType !== ClaimType.Disability) {
        return (
            <React.Fragment>
                <Grid container justify="space-between" className={classes.marginTop2}>
                    <Grid container item xs={10} alignItems="center">
                        <Typography
                            style={{ padding: 10 }}
                            className={classes.heading}
                            variant="body1"
                        >
                            Personal Details
                        </Typography>
                    </Grid>
                </Grid>
                <Paper className={classes.paper}>
                    <Grid container item xs={12} alignItems="center" justify="flex-end">
                        <Tooltip title="Edit Personal Details">
                            <Button
                                id="EditPersonalDetails"
                                color="secondary"
                                onClick={edit}
                                disabled={!disableEditButton}
                            >
                                <Edit />
                            </Button>
                        </Tooltip>
                    </Grid>
                    <Grid container justify="flex-start">
                        <Grid item xs={12} container justify="flex-start">
                            <Grid item xs={12} lg={12}>
                                <LabeledValue label="Policyholder type" value={policyHolderType} />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Title"
                                    value={
                                        personalDetails?.title || personalDetails?.lifeAssuredTitle
                                    }
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="First Name"
                                    value={
                                        personalDetails?.firstName ||
                                        personalDetails?.lifeAssuredFirstName
                                    }
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Surname"
                                    value={
                                        personalDetails?.surname ||
                                        personalDetails?.lifeAssuredSurname
                                    }
                                />
                            </Grid>
                            {personalDetails?.maiden && (
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Maiden Name"
                                        value={personalDetails?.maiden}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="Gender"
                                value={
                                    personalDetails?.gender || personalDetails?.lifeAssuredGender
                                }
                            />
                        </Grid>
                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="Marital Status"
                                value={
                                    personalDetails?.maritalStatus ||
                                    personalDetails?.lifeAssuredMaritalStatus
                                }
                            />
                        </Grid>
                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="SA Resident"
                                value={
                                    personalDetails?.isRSAResident ||
                                    personalDetails?.lifeAssuredIsRSAResident
                                }
                            />
                        </Grid>
                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="ID Number"
                                value={
                                    personalDetails?.idNumber ||
                                    personalDetails?.lifeAssuredIdNumber
                                }
                            />
                        </Grid>
                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="Date of Birth"
                                value={
                                    getDisplayDate(personalDetails?.dateOfBirth) ||
                                    getDisplayDate(personalDetails?.lifeAssuredDateOfBirth)
                                }
                            />
                        </Grid>
                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="Place of Birth"
                                value={
                                    personalDetails?.placeOfBirth ||
                                    personalDetails?.lifeAssuredPlaceOfBirth
                                }
                            />
                        </Grid>
                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="Country of Birth"
                                value={
                                    personalDetails?.countryOfBirth ||
                                    personalDetails?.lifeAssuredCountryOfBirth
                                }
                            />
                        </Grid>
                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="Country of residence"
                                value={
                                    personalDetails?.countryOfResidence ||
                                    personalDetails?.lifeAssuredCountryOfResidence
                                }
                            />
                        </Grid>
                        {(personalDetails?.incomeTaxNumber ||
                            personalDetails?.lifeAssuredIncomeTaxNumber) && (
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="SA Income tax number"
                                    value={
                                        personalDetails?.incomeTaxNumber ||
                                        personalDetails?.lifeAssuredIncomeTaxNumber
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            </React.Fragment>
        );
    } else {
        return (
            <React.Fragment>
                <Grid container justify="space-between" className={classes.marginTop2}>
                    <Grid container item xs={10} alignItems="center">
                        <Typography
                            style={{ padding: 10 }}
                            className={classes.heading}
                            variant="body1"
                        >
                            {header}
                        </Typography>
                    </Grid>
                </Grid>
                <Paper className={classes.paper}>
                    <Grid container item xs={12} alignItems="center" justify="flex-end">
                        <Tooltip title="Edit Personal Details">
                            <Button
                                id="EditPersonalDetails"
                                color="secondary"
                                onClick={edit}
                                disabled={!disableEditButton}
                            >
                                <Edit />
                            </Button>
                        </Tooltip>
                    </Grid>
                    {policyHolderType === 'Individual' && (
                        <Grid container justify="flex-start">
                            <Grid item xs={12} container justify="flex-start">
                                <Grid item xs={12} lg={12}>
                                    <LabeledValue
                                        label="Is the policyholder an individual or company"
                                        value={policyHolderType}
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Title"
                                        value={
                                            personalDetails?.title ||
                                            personalDetails?.lifeAssuredTitle
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Full first Name"
                                        value={
                                            personalDetails?.firstName ||
                                            personalDetails?.lifeAssuredFirstName
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Surname"
                                        value={
                                            personalDetails?.surname ||
                                            personalDetails?.lifeAssuredSurname
                                        }
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Cellphone number"
                                        value={contactDetails?.cellphoneNumber}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Email address"
                                    value={contactDetails?.emailAddress}
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Has your marital status changed"
                                    value={personalDetails?.hasMaritalStatusChanged}
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Marital Status"
                                    value={
                                        personalDetails?.maritalStatus ||
                                        personalDetails?.lifeAssuredMaritalStatus
                                    }
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Date of marriage"
                                    value={getDisplayDate(personalDetails?.dateOfMarriage)}
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Are you a South African resident?"
                                    value={personalDetails?.lifeAssuredIsRSAResident}
                                />
                            </Grid>
                            {personalDetails?.lifeAssuredIsRSAResident === 'true' && (
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="ID Number"
                                        value={
                                            personalDetails?.idNumber ||
                                            personalDetails?.lifeAssuredIdNumber
                                        }
                                    />
                                </Grid>
                            )}
                            {personalDetails?.lifeAssuredIsRSAResident === 'false' && (
                                <>
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Passport Number"
                                            value={
                                                personalDetails?.passportNumber ||
                                                personalDetails?.lifeAssuredPassportNumber
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Country of residence"
                                            value={
                                                personalDetails?.countryOfResidence ||
                                                personalDetails?.lifeAssuredCountryOfResidence
                                            }
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    )}
                    {isThePolicyHolderAnIndividualOrCompany === 'Company' && (
                        <Grid container justify="flex-start">
                            <Grid item xs={12} container justify="flex-start">
                                <Grid item xs={12} lg={12}>
                                    <LabeledValue
                                        label="Is the policyholder an individual or company"
                                        value={isThePolicyHolderAnIndividualOrCompany}
                                    />
                                </Grid>
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Company trading name"
                                        value={
                                            companyDetails?.companyName ||
                                            companyDetails?.phCompanyName
                                        }
                                    />
                                </Grid>

                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Company registration number"
                                        value={
                                            companyDetails?.companyRegistrationNumber ||
                                            companyDetails?.phCompanyRegistrationNumber
                                        }
                                    />
                                </Grid>
                                {companyDetails && (
                                    <Grid item xs={4} lg={3}>
                                        <LabeledValue
                                            label="Company registration date"
                                            value={getDisplayDate(
                                                companyDetails.companyRegistrationDate
                                            )}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={4} lg={3}>
                                    <LabeledValue
                                        label="Coutry of company"
                                        value={
                                            companyDetails?.countryOfIncorporation ||
                                            companyDetails?.phCountryOfIncorporation
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Paper>
            </React.Fragment>
        );
    }
};

const mapStateToProps = (state: State, ownProps: BaseProps) => ({
    formDetails: ownProps.formDetails ?? getFormDetails(state),
    disableEditButton: ownProps.disableEditButton ?? false,
    updateFormDetails: ownProps.updateFormDetails ?? updateFormDetailsAction,
    claimType: getClaim(state).claimType,
});

const mapDispatchToProps = {
    openModal: openModalAction,
    updateFormDetails: updateFormDetailsAction,
};

export const PersonalDetailsView = connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(personalDetailsView));
